/**
 * The conversation id this page load started with, restored from sessionStorage by
 * redux-persist (the `conversation` slice is persisted, messages included).
 *
 * `useConversationPageFacade` skips loading history for the conversation it just created and is
 * still streaming into, because the server has not saved that turn yet. It recognised that
 * conversation by `conversationState.conversationId === URL id`, which is also true after every
 * reload, since the id comes back from sessionStorage. So a reload never loaded history: the page
 * showed the messages saved in the tab, and turns made in another tab or device never appeared
 * (seen on test: turns 32-35 missing, the server had all 37).
 *
 * Kept outside React: it must survive the page component remounting while a new conversation
 * navigates to its URL, and reset on a real reload.
 */
let restored: { id: string | null } | null = null;

/**
 * The id restored on this page load. The first call records it (PersistGate renders only after
 * rehydration, so the first render sees the restored state).
 */
export function conversationIdRestoredOnLoad(current: string | null | undefined): string | null {
  if (restored === null) restored = { id: current ?? null };
  return restored.id;
}

/** Its history was loaded from the server; from here on it is treated like any other. */
export function forgetRestoredConversation(): void {
  if (restored) restored.id = null;
}

/** Tests only. */
export function resetRestoredConversationForTests(): void {
  restored = null;
}
